import { Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router-v6";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import "@/i18n";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { BannersListPage } from "@/pages/banners/list";
import { BonusesPage } from "@/pages/bonuses/list";
import { CategoriesListPage } from "@/pages/categories/list";
import { ClientShowPage } from "@/pages/clients/show";
import { ClientsListPage } from "@/pages/clients/list";
import { DashboardPage } from "@/pages/dashboard";
import { LoginPage } from "@/pages/login";
import { OrderShowPage } from "@/pages/orders/show";
import { OrdersListPage } from "@/pages/orders/list";
import { ProductEditPage } from "@/pages/products/edit";
import { ProductsListPage } from "@/pages/products/list";
import { PromoCodesListPage } from "@/pages/promo-codes/list";
import { ReviewsListPage } from "@/pages/reviews/list";
import { SettingsPage } from "@/pages/settings";
import { SiteContentPage } from "@/pages/site-content";
import { StockLogPage } from "@/pages/stock-log/list";
import { StoresListPage } from "@/pages/stores/list";
import { ConfigurationPage } from "@/pages/configuration/index";
import { StaffGroupsPage } from "@/pages/configuration/staff_groups";
import { StaffUsersPage } from "@/pages/configuration/staff_users";
import { ChannelsPage } from "@/pages/configuration/channels";
import {
  PagesCmsPage,
  ProductTypesPage,
  PromotionsPage,
  VouchersPage,
  WebhooksPage,
} from "@/pages/configuration/scaffolds";
import { AttributesPage } from "@/pages/configuration/attributes";
import { RefundsPage } from "@/pages/configuration/refunds";
import { BrandsPage } from "@/pages/configuration/brands";
import { AiContentPage } from "@/pages/configuration/ai_content";
import { AiContentRunPage } from "@/pages/configuration/ai_content_run";
import { CatalogImportPage } from "@/pages/configuration/catalog_import";
import { DesignTokensPage } from "@/pages/configuration/design_tokens";
import { NavigationPage } from "@/pages/configuration/navigation";
import { FooterEditorPage } from "@/pages/configuration/footer";
import { CommandPalette } from "@/components/search/CommandPalette";
import { accessControlProvider } from "@/providers/access-control";
import { authProvider } from "@/providers/auth";
import { dataProvider } from "@/providers/data";
import { i18nProvider } from "@/providers/i18n";
import { liveProvider } from "@/providers/live";
import { notificationProvider } from "@/providers/notification";

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        notificationProvider={notificationProvider}
        i18nProvider={i18nProvider}
        liveProvider={liveProvider}
        routerProvider={routerProvider}
        resources={[
          {
            name: "dashboard",
            list: "/",
            meta: { label: "Дашборд" },
          },
          {
            name: "orders",
            list: "/orders",
            show: "/orders/:id",
            meta: { label: "Заказы" },
          },
          {
            name: "products",
            list: "/products",
            create: "/products/new",
            edit: "/products/:id/edit",
            show: "/products/:id",
            meta: { label: "Товары", canDelete: true },
          },
          {
            name: "categories",
            list: "/categories",
            edit: "/categories/:id/edit",
            meta: { label: "Категории" },
          },
          {
            name: "clients",
            list: "/clients",
            show: "/clients/:id",
            meta: { label: "Клиенты" },
          },
          {
            name: "banners",
            list: "/banners",
            meta: { label: "Баннеры" },
          },
          {
            name: "reviews",
            list: "/reviews",
            meta: { label: "Отзывы" },
          },
          {
            name: "promo-codes",
            list: "/promo-codes",
            meta: { label: "Промокоды" },
          },
          {
            name: "stock-log",
            list: "/stock-log",
            meta: { label: "Склад" },
          },
          {
            name: "bonuses",
            list: "/bonuses",
            meta: { label: "Бонусы" },
          },
          {
            name: "stores",
            list: "/stores",
            meta: { label: "Магазины" },
          },
          {
            name: "settings",
            list: "/settings",
            meta: { label: "Настройки" },
          },
          {
            name: "configuration",
            list: "/configuration",
            meta: { label: "Конфигурация" },
          },
        ]}
        options={{
          syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          useNewQueryKeys: true,
          disableTelemetry: true,
          liveMode: "auto",
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route element={<ProtectedRoute />}>
            <Route index element={<DashboardPage />} />
            <Route path="orders" element={<OrdersListPage />} />
            <Route path="orders/:id" element={<OrderShowPage />} />
            <Route path="products" element={<ProductsListPage />} />
            <Route path="products/new" element={<ProductEditPage />} />
            <Route path="products/:id/edit" element={<ProductEditPage />} />
            <Route path="categories" element={<CategoriesListPage />} />
            <Route path="clients" element={<ClientsListPage />} />
            <Route path="clients/:id" element={<ClientShowPage />} />
            <Route path="banners" element={<BannersListPage />} />
            <Route path="reviews" element={<ReviewsListPage />} />
            <Route path="promo-codes" element={<PromoCodesListPage />} />
            <Route path="stock-log" element={<StockLogPage />} />
            <Route path="bonuses" element={<BonusesPage />} />
            <Route path="stores" element={<StoresListPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="configuration" element={<ConfigurationPage />} />
            <Route path="configuration/staff-users" element={<StaffUsersPage />} />
            <Route path="configuration/staff-groups" element={<StaffGroupsPage />} />
            <Route path="configuration/channels" element={<ChannelsPage />} />
            <Route path="configuration/product-types" element={<ProductTypesPage />} />
            <Route path="configuration/attributes" element={<AttributesPage />} />
            <Route path="configuration/pages" element={<PagesCmsPage />} />
            <Route path="configuration/promotions" element={<PromotionsPage />} />
            <Route path="configuration/vouchers" element={<VouchersPage />} />
            <Route path="configuration/webhooks" element={<WebhooksPage />} />
            <Route path="configuration/refunds" element={<RefundsPage />} />
            <Route path="configuration/brands" element={<BrandsPage />} />
            <Route path="configuration/catalog-import" element={<CatalogImportPage />} />
            <Route path="configuration/ai-content" element={<AiContentPage />} />
            <Route path="configuration/ai-content/:runId" element={<AiContentRunPage />} />
            <Route path="configuration/navigation" element={<NavigationPage />} />
            <Route path="configuration/footer" element={<FooterEditorPage />} />
            <Route path="configuration/design-tokens" element={<DesignTokensPage />} />
            <Route path="site-content" element={<SiteContentPage />} />
            <Route path="profile" element={<ProfilePlaceholder />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <CommandPalette />
        <Toaster position="top-right" richColors closeButton />
      </Refine>
    </BrowserRouter>
  );
}

function ProfilePlaceholder(): JSX.Element {
  return (
    <div className="text-center py-20 text-muted-foreground">
      Profile page — coming in Phase 4
    </div>
  );
}
